import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import MuiSelect from "@mui/material/Select";
import MuiTypography from "@mui/material/Typography";
import { cleanProps } from "../internal/cleanProps";
import { fieldErrorSx, fieldHelpSx, fieldLabelSx } from "../internal/field-styles";
import { tokens } from "../tokens";
import type { SelectProps } from "../types";

/**
 * Containment note (types.ts): the upstream `Value` generic is forwarded
 * but bounded to `extends string` — it can never resolve to `unknown`.
 * Native mode, multiple selection, `MenuProps` and `autoWidth` are fixed
 * (none is a parameter on this adapter's type).
 *
 * ADR-20261102-h2fd: the H2 design system's Select inside its FormField, the same label, help
 * and error treatment as TextField. `emptyLabel` is H2's placeholder: shown while nothing is
 * selected and offered as the first option.
 */
export function Select<Value extends string = string>({
  id,
  label,
  value,
  onChange,
  options,
  help,
  emptyLabel,
  errorText,
  error,
  required,
  disabled,
  fullWidth = true,
  name,
  testId,
}: Readonly<SelectProps<Value>>) {
  const helpId = help !== undefined ? `${id}-help` : undefined;
  const errorId = error === true && errorText !== undefined ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter((part): part is string => part !== undefined).join(" ") || undefined;
  const labelId = `${id}-label`;

  return (
    <FormControl sx={{ mb: `${tokens.spacing.lg}px` }} {...cleanProps({ fullWidth, error, required, disabled })}>
      <FormLabel id={labelId} htmlFor={id} sx={fieldLabelSx}>
        {label}
      </FormLabel>
      {help !== undefined ? (
        <MuiTypography id={helpId} component="p" variant="caption" sx={fieldHelpSx}>
          {help}
        </MuiTypography>
      ) : null}
      <MuiSelect
        id={id}
        labelId={labelId}
        value={value}
        displayEmpty={emptyLabel !== undefined}
        onChange={(event) => {
          onChange(event.target.value as Value);
        }}
        {...cleanProps({ name, "aria-describedby": describedBy, "data-testid": testId })}
      >
        {emptyLabel !== undefined ? <MenuItem value="">{emptyLabel}</MenuItem> : null}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} {...cleanProps({ disabled: option.disabled })}>
            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>
      {errorId !== undefined ? (
        <MuiTypography id={errorId} component="p" variant="caption" sx={fieldErrorSx}>
          {errorText}
        </MuiTypography>
      ) : null}
    </FormControl>
  );
}
